import React, { useState } from "react";
import { useForm, FormProvider } from "react-hook-form";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  makeStyles
} from "@material-ui/core";
import SendIcon from "react-icons/lib/md/send";
import { ThemeType } from "@theme";
import { PrimaryButton, RegularButton, SpinnerOverlay } from "@components";
import { Subheading } from "@components/typography/Subheading";

import {
  TextEditor,
  TextEditorData,
  TextEditorProps,
  TextEditorTemplate
} from "./TextEditor";
import { TemplateSelector } from "./TemplateSelector";
import locale from "@bonliva-crm/locale";
import { getSmsTextHint, translateSmsTextError } from "@components/sms/sms-text-hint";

type Props = {
  open: boolean;
  title?: string;
  initialValues?: TextEditorData;
  onClose?: () => any;
  onSubmit: (values: TextEditorData) => Promise<any>;
  templates?: TextEditorTemplate[];
  TextEditorProps?: Partial<TextEditorProps>;
  parseTemplate?: (tpl: string) => string;
};

export function TextEditorDialog(props: Props) {
  const classes = useStyles({});
  const [isTemplateSelectorOpen, setIsTemplateSelectorOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const {
    initialValues = { from: "", to: "", message: "" },
    onClose,
    onSubmit,
    TextEditorProps: TextEditorPropsProp = {},
    templates = []
  } = props;

  const form = useForm<TextEditorData>({
    defaultValues: initialValues
  });

  const { isSubmitting } = form.formState;
  const message = form.watch("message");
  // Only the SMS mass-contact dialog uses this editor, so the message is held
  // to the SMS rule the API enforces (CRMDEV-7459).
  const smsHint = getSmsTextHint(message);
  const canSend = !isSubmitting && message && smsHint.valid;
  const canClose = !isSubmitting;

  const handleSubmit = form.handleSubmit(async (values) => {
    try {
      await onSubmit(values);
    } catch (e) {
      setError(translateSmsTextError(e) ?? e.toString());
    }
  });

  const onSelect = (template: TextEditorTemplate) => {
    const text = props.parseTemplate
      ? props.parseTemplate(template.content)
      : template.content;
    form.setValue("message", `${form.getValues("message")}${text}`);
    setIsTemplateSelectorOpen(false);
  };

  return (
    <FormProvider {...form}>
      <Dialog
        open={Boolean(props.open)}
        classes={{ paper: classes.root }}
        onClose={canClose ? onClose : undefined}
      >
        {props.title && <DialogTitle>{props.title}</DialogTitle>}

        <DialogContent>
          <TextEditor
            {...TextEditorPropsProp}
            templates={templates}
            onTemplateSelect={onSelect}
            getMessageLength={() => smsHint.counter}
          />

          {smsHint.error && (
            <Subheading color="error" style={{ marginTop: 24 }}>
              {smsHint.error}
            </Subheading>
          )}

          <Dialog
            color="error"
            open={Boolean(error)}
            onClose={() => setError(null)}
          >
            <DialogContent>
              <Subheading color="error">{error}</Subheading>
            </DialogContent>
          </Dialog>
        </DialogContent>

        <DialogActions>
          {templates.length > 3 && (
            <RegularButton
              style={{ marginRight: "auto" }}
              variant="text"
              onClick={() => setIsTemplateSelectorOpen(true)}
            >
              {locale.AllTemplates}
            </RegularButton>
          )}

          <RegularButton onClick={onClose} disabled={!canClose} variant="text">
            {locale.Close}
          </RegularButton>

          <PrimaryButton
            leftIcon={<SendIcon />}
            onClick={handleSubmit}
            disabled={!canSend}
            variant="text"
          >
            {locale.Send}
          </PrimaryButton>
        </DialogActions>

        <TemplateSelector
          open={isTemplateSelectorOpen}
          onClose={() => setIsTemplateSelectorOpen(false)}
          onSelect={onSelect}
          templates={templates.map((t) => ({
            ...t,
            content: props.parseTemplate(t.content)
          }))}
        />

        <SpinnerOverlay show={isSubmitting} />
      </Dialog>
    </FormProvider>
  );
}

const useStyles = makeStyles((theme: ThemeType) => ({
  root: {
    minWidth: 400
  }
}));
